import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Divider,
  Paper,
  LinearProgress,
  Chip,
  MenuItem,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  CardActions,
  Snackbar,
  Alert,
  Pagination,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import {
  Edit as EditIcon,
  AccountBalance as LoanIcon,
  CalendarMonth as CalendarIcon,
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Payment as PaymentIcon,
  Add as AddIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  TableChart as TableChartIcon
} from '@mui/icons-material';
import { supabase } from '../supabaseClient';
import { formatCurrency, formatPercentage, calculateEMI } from '../utils/formatters';
import { format, differenceInDays, differenceInMonths, addMonths } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { calculateLoanDetails, calculateLoanEnd } from '../utils/loanCalculations';
import GuidedTour from '../components/tour/GuidedTour';
import TourButton from '../components/tour/TourButton';
import useTour from '../components/tour/useTour';
import { TOURS } from '../components/tour/tours';
import AmountField from '../components/AmountField';

// Add these constants at the top of the file
const PAYMENT_STATUS = {
  COMPLETED: 'completed',
  OVERDUE: 'overdue',
  DUE_SOON: 'due_soon',
  UPCOMING: 'upcoming'
};

const DUE_SOON_DAYS = 5; // Consider "due soon" if within 5 days

// Add this constant at the top with other constants
const LOAN_SETTLEMENT_TYPES = {
  EMI: 'emi',
  ONE_TIME: 'one_time'
};

// style for  number input
const numberInputStyle = {
  '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '& input[type=number]': {
    '-moz-appearance': 'textfield',
  },
};

// Add this utility function at the top of your file
const calculateEndDate = (startDate, tenureMonths) => {
  if (!startDate || !tenureMonths) return null;
  // date-fns addMonths clamps to month end (31 Jan + 1 month = 28/29 Feb, not 3 Mar)
  return addMonths(new Date(startDate), parseInt(tenureMonths));
};

// Dates are stored as 'YYYY-MM-DD'. Format with local date parts: toISOString() is UTC and
// shifts dates picked at local midnight back by one day in timezones ahead of UTC (e.g. IST).
const toDateString = (date) => {
  if (!date) return null;
  const d = new Date(date);
  return isNaN(d.getTime()) ? null : format(d, 'yyyy-MM-dd');
};

// Parse a stored 'YYYY-MM-DD' as a local date (new Date('YYYY-MM-DD') is UTC midnight)
const parseDateOnly = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(value);
};

// Advance (pre)payments are not EMI instalments and must not move the EMI schedule
const countEmiPayments = (payments) =>
  (payments || []).filter(p => p.payment_type !== 'advance').length;

// Update the calculateNextPaymentDate function
const calculateNextPaymentDate = (startDate) => {
  if (!startDate) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const originalStartDate = new Date(startDate);
  originalStartDate.setHours(0, 0, 0, 0);

  // If start date is in the future, return start date
  if (originalStartDate > today) {
    return originalStartDate;
  }

  // Get the payment day from start date
  const paymentDay = originalStartDate.getDate();

  // Calculate months between start date and today
  const monthsDiff = (today.getFullYear() - originalStartDate.getFullYear()) * 12 +
    (today.getMonth() - originalStartDate.getMonth());

  // Calculate the next payment date
  const nextPaymentDate = new Date(originalStartDate);
  nextPaymentDate.setMonth(originalStartDate.getMonth() + monthsDiff + 1);
  nextPaymentDate.setDate(paymentDay);

  // If the calculated date is in the past, move to next month
  if (nextPaymentDate <= today) {
    nextPaymentDate.setMonth(nextPaymentDate.getMonth() + 1);
  }

  // Handle edge case where payment day doesn't exist in the target month
  const targetMonth = nextPaymentDate.getMonth();
  nextPaymentDate.setDate(paymentDay);
  if (nextPaymentDate.getMonth() !== targetMonth) {
    nextPaymentDate.setDate(0); // Last day of the previous month
  }

  return nextPaymentDate;
};

// Update the calculateTotalLoanAmount function with proper compound interest calculation
const calculateTotalLoanAmount = (principal, interestRate, tenureMonths) => {
  if (!principal || !interestRate || !tenureMonths) return principal;

  // Convert annual interest rate to monthly
  const monthlyRate = interestRate / 100 / 12;

  // Calculate total amount with compound interest
  // Formula: A = P(1 + r)^n
  // where:
  // A = Total amount
  // P = Principal amount
  // r = Monthly interest rate
  // n = Number of months
  const totalAmount = principal * Math.pow(1 + monthlyRate, tenureMonths);

  return totalAmount;
};

// Add a new utility function to calculate interest amount
const calculateInterestAmount = (principal, interestRate, tenureMonths) => {
  if (!principal || !interestRate || !tenureMonths) return 0;

  const totalAmount = calculateTotalLoanAmount(principal, interestRate, tenureMonths);
  return totalAmount - principal;
};

// Add this utility function to calculate months between dates
const calculateMonthsBetweenDates = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;

  const start = new Date(startDate);
  const end = new Date(endDate);

  const years = end.getFullYear() - start.getFullYear();
  const months = end.getMonth() - start.getMonth();

  return (years * 12) + months;
};

const Loans = () => {

  const formatDateToDDMMYYYY = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const [loading, setLoading] = useState(true);
  const [loans, setLoans] = useState([]);
  const [filteredLoans, setFilteredLoans] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('active'); // Change default from 'all' to 'active'
  const [sortBy, setSortBy] = useState('name'); // Default sort by name
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState(null);
  const loansTour = useTour('loans', { ready: !loading && !error });
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedLoanForPayment, setSelectedLoanForPayment] = useState(null);
  // Multi-row payment modal state
  const [paymentRows, setPaymentRows] = useState([
    { amount: '', date: new Date(), note: '' }
  ]);

  // Add a new payment row, default amount same as first row
  const handleAddPaymentRow = () => {
    setPaymentRows((prev) => {
      const firstAmount = prev[0]?.amount || '';
      return [...prev, { amount: firstAmount, date: new Date(), note: '' }];
    });
  };


  // Remove a payment row by index
  const handleRemovePaymentRow = (idx) => {
    setPaymentRows((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
  };

  // Handle changes in any row field
  const handlePaymentRowChange = (idx, key, value) => {
    setPaymentRows((prev) => prev.map((row, i) =>
      i === idx ? { ...row, [key]: value } : row
    ));
  };

  // Submit all payments in the modal
  const handleMakeMultiplePayments = async () => {
    // Prevent double submission
    if (loading) return;
    try {
      setLoading(true);
      if (!selectedLoanForPayment) throw new Error('No loan selected');
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      const enteredRows = paymentRows.filter(row => String(row.amount ?? '').trim() !== '');
      if (enteredRows.some(row => isNaN(parseFloat(row.amount)) || parseFloat(row.amount) <= 0)) {
        throw new Error('Payment amounts must be greater than zero.');
      }
      if (enteredRows.some(row => !row.date || isNaN(new Date(row.date).getTime()))) {
        throw new Error('Please enter a valid payment date for each payment.');
      }
      const validRows = enteredRows;
      if (validRows.length === 0) throw new Error('Please enter at least one valid payment.');

      // Insert all payments
      const paymentsToInsert = validRows.map(row => ({
        loan_id: selectedLoanForPayment.id,
        user_id: user.id,
        amount: parseFloat(row.amount),
        payment_date: toDateString(row.date),
        description: row.note || '',
        payment_type: 'manual',
        created_at: new Date().toISOString(),
      }));
      const { error: insertError } = await supabase.from('loan_payments').insert(paymentsToInsert);
      if (insertError) throw insertError;

      // Update next payment date for EMI loans with overpayment handling
      if (selectedLoanForPayment.settlement_type === LOAN_SETTLEMENT_TYPES.EMI) {
        // Calculate total payment amount
        const totalPaymentAmount = validRows.reduce((sum, row) => sum + parseFloat(row.amount), 0);
        const emiAmount = parseFloat(selectedLoanForPayment.emi_amount || 0);
        
        // Calculate how many EMIs this payment covers (including overpayments)
        let emisCovered = 0;
        if (emiAmount > 0) {
          emisCovered = Math.floor(totalPaymentAmount / emiAmount);
          // If there's a significant remainder (>50% of EMI), count it as partial EMI
          const remainder = totalPaymentAmount % emiAmount;
          if (remainder > emiAmount * 0.5) {
            emisCovered += 1;
          }
        } else {
          // Fallback to number of payment rows if EMI amount is not set
          emisCovered = validRows.length;
        }

        // Advance next payment date by the number of EMIs covered
        const startDate = parseDateOnly(selectedLoanForPayment.start_date);
        const currentPayments = countEmiPayments(selectedLoanForPayment.payments);
        const newNextPayment = addMonths(startDate, currentPayments + emisCovered + 1);

        const { error: updateError } = await supabase
          .from('loans')
          .update({ next_payment_date: toDateString(newNextPayment) })
          .eq('id', selectedLoanForPayment.id);

        if (updateError) {
          console.error('Error updating next payment date:', updateError);
        }

        // Show detailed success message for overpayments
        let successMessage = `Successfully recorded ${paymentsToInsert.length} payment(s)!`;
        if (emisCovered > validRows.length) {
          successMessage += ` This payment covers ${emisCovered} EMI(s) due to overpayment.`;
        }
        
        setStatusSnackbar({
          open: true,
          message: successMessage,
          severity: 'success',
        });
      } else {
        setStatusSnackbar({
          open: true,
          message: `Successfully recorded ${paymentsToInsert.length} payment(s)!`,
          severity: 'success',
        });
      }

      await fetchLoans();
      setPaymentDialogOpen(false);
      setPaymentRows([{ amount: '', date: new Date(), note: '' }]);
    } catch (error) {
      setStatusSnackbar({
        open: true,
        message: `Failed to record payment(s): ${error.message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const [statusSnackbar, setStatusSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [newLoanDialogOpen, setNewLoanDialogOpen] = useState(false);
  const [newLoan, setNewLoan] = useState({
    name: '',
    type: 'personal',
    principal_amount: '',
    interest_rate: '',
    tenure_months: '',
    emi_amount: '',
    start_date: new Date(),
    end_date: '',
    lender: '',
    remaining_amount: '',
    next_payment_date: new Date(),
    payment_day: '',
    description: '',
    settlement_type: LOAN_SETTLEMENT_TYPES.EMI,
    interest_type: 'diminishing',
    due_date: null
  });

  // Auto-calculate next payment date based on start date
  useEffect(() => {
    if (newLoan.start_date && newLoan.settlement_type === LOAN_SETTLEMENT_TYPES.EMI) {
      const nextPayment = addMonths(new Date(newLoan.start_date), 1);
      setNewLoan(prev => ({ ...prev, next_payment_date: nextPayment }));
    }
  }, [newLoan.start_date, newLoan.settlement_type]);
  const [paymentHistoryDialogOpen, setPaymentHistoryDialogOpen] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);

  // Add these new state variables at the top of your component
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPaymentDialogOpen, setEditPaymentDialogOpen] = useState(false);

  // Add with other state declarations
  const [filterLoanType, setFilterLoanType] = useState('all');

  // Add new state for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loanToDelete, setLoanToDelete] = useState(null);

  // Add state for amortization dialog
  const [amortizationDialogOpen, setAmortizationDialogOpen] = useState(false);
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);

  // Add state for advance payment dialog
  const [advancePaymentDialogOpen, setAdvancePaymentDialogOpen] = useState(false);
  const [advancePaymentData, setAdvancePaymentData] = useState({
    loan: null,
    amount: '',
    option: 'reduce_emi', // 'reduce_emi' or 'reduce_tenure'
    newEMI: 0,
    newTenure: 0,
    newEndDate: null
  });

  // Add state for delete payment confirmation
  const [deletePaymentDialogOpen, setDeletePaymentDialogOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);

  // Add sort options
  const sortOptions = [
    { value: 'name', label: 'Name (A to Z)' },
    { value: 'principal_amount', label: 'Principal Amount' },
    { value: 'interest_rate', label: 'Interest Rate' },
    { value: 'remaining_amount', label: 'Remaining Amount' },
    { value: 'next_payment_date', label: 'Next Payment Date' },
    { value: 'emi_amount', label: 'EMI Amount' },
    { value: 'tenure_months', label: 'Tenure' },
    { value: 'start_date', label: 'Start Date' }
  ];

  const loanTypes = [
    { value: 'personal', label: 'Personal Loan' },
    { value: 'home', label: 'Home Loan' },
    { value: 'car', label: 'Car Loan' },
    { value: 'education', label: 'Education Loan' },
    { value: 'business', label: 'Business Loan' },
    { value: 'gold', label: 'Gold Loan' },
    { value: 'other', label: 'Other' }
  ];

  const getLoanTypeLabel = (type) => {
    const loanType = loanTypes.find(lt => lt.value === type);
    return loanType ? loanType.label : 'Unknown Loan';
  };

  const fetchLoans = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) {
        console.error('User authentication error:', userError);
        throw new Error(`Authentication error: ${userError.message}`);
      }
      if (!user) {
        console.error('No user found in session');
        throw new Error('Please sign in to view your loans');
      }

      console.log('Fetching loans for user:', user.id);

      // First fetch all loans for the user
      const { data: loansData, error: loansError } = await supabase
        .from('loans')
        .select('*')
        .eq('user_id', user.id);

      if (loansError) {
        console.error('Error fetching loans:', loansError);
        throw new Error(`Failed to fetch loans: ${loansError.message}`);
      }

      if (!loansData) {
        console.warn('No loans data returned');
        setLoans([]);
        setFilteredLoans([]);
        setLoading(false);
        return;
      }

      // Then fetch all payments for these loans
      const { data: paymentsData, error: paymentsError } = await supabase
        .from('loan_payments')
        .select('*')
        .in('loan_id', loansData.map(loan => loan.id));

      if (paymentsError) {
        console.error('Error fetching payments:', paymentsError);
        throw new Error(`Failed to fetch payments: ${paymentsError.message}`);
      }

      console.log('Fetched loans:', loansData.length);
      console.log('Fetched payments:', paymentsData?.length || 0);

      // Process the data using the new calculation engine
      const processedLoans = loansData.map(loan => {
        const loanPayments = paymentsData?.filter(p => p.loan_id === loan.id) || [];
        const details = calculateLoanDetails(loan, loanPayments);

        // Calculate total interest and amount based on settlement type
        let totalInterestAtEnd = 0;
        let finalAmountAtEnd = loan.principal_amount;

        if (loan.settlement_type === LOAN_SETTLEMENT_TYPES.EMI) {
          // Always use diminishing balance method for EMI loans
          const startDate = new Date(loan.start_date);
          const endDate = new Date(loan.end_date);
          const result = calculateLoanEnd(
            loan.principal_amount,
            loan.interest_rate,
            startDate,
            endDate,
            parseFloat(loan.emi_amount)
          );
          totalInterestAtEnd = result.totalInterest;
          finalAmountAtEnd = result.finalAmount;
        } else {
          // For ONE_TIME settlement with quarterly compounding
          const startDate = new Date(loan.start_date);
          const dueDate = new Date(loan.due_date);

          // Calculate the number of days between start and due date
          const days = Math.floor((dueDate - startDate) / (1000 * 60 * 60 * 24));
          const numQuarters = days / 90; // Convert days to quarters (90 days per quarter)

          // Convert annual rate to quarterly
          const annualRate = loan.interest_rate / 100;
          const quarterlyRate = annualRate / 4;

          // Calculate interest using continuous quarterly compounding
          // For partial quarters, we'll use the exact time fraction
          totalInterestAtEnd = loan.principal_amount * (Math.pow(1 + quarterlyRate, numQuarters) - 1);
        }

        finalAmountAtEnd = loan.principal_amount + totalInterestAtEnd;

        const totalAmount = loan.principal_amount + details.totalInterestAccrued;
        const remainingAmount = Math.max(0, totalAmount - details.totalPaid);
        const completionPercentage = totalAmount > 0 ? (details.totalPaid / totalAmount) * 100 : 0;

        let paymentStatus = PAYMENT_STATUS.UPCOMING;
        let daysUntilDue = 0;
        let calculatedNextPaymentDate = loan.next_payment_date;

        if (remainingAmount <= 0) {
          paymentStatus = PAYMENT_STATUS.COMPLETED;
        } else {
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          let targetDate;
          if (loan.settlement_type === LOAN_SETTLEMENT_TYPES.ONE_TIME) {
            targetDate = loan.due_date ? parseDateOnly(loan.due_date) : new Date(loan.due_date);
          } else {
            // For EMI loans, calculate the correct next payment date based on payments made
            const startDate = parseDateOnly(loan.start_date);
            const paymentsMade = countEmiPayments(loanPayments);
            const nextPaymentDate = addMonths(startDate, paymentsMade + 1);

            // Update the calculated next payment date
            calculatedNextPaymentDate = toDateString(nextPaymentDate);
            targetDate = nextPaymentDate;
          }

          targetDate.setHours(0, 0, 0, 0);
          daysUntilDue = Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24));

          if (daysUntilDue < 0) {
            paymentStatus = PAYMENT_STATUS.OVERDUE;
          } else if (daysUntilDue <= DUE_SOON_DAYS) {
            paymentStatus = PAYMENT_STATUS.DUE_SOON;
          }
        }

        return {
          ...loan,
          ...details,
          totalInterestAtEnd,
          finalAmountAtEnd,
          totalAmount,
          remaining_amount: remainingAmount,
          completionPercentage,
          paymentStatus,
          daysUntilDue,
          payments: loanPayments,
          next_payment_date: calculatedNextPaymentDate,
        };
      });

      console.log('Processed loans:', processedLoans);
      setLoans(processedLoans);
      setFilteredLoans(processedLoans);
      setLoading(false);
    } catch (error) {
      console.error('Error in fetchLoans:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        if (isMounted) {
          await fetchLoans();
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error in useEffect:', error);
          setError(error.message);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Apply filters, search, and sorting
  const applyFilters = (loans, query, filter, loanType, sort, order) => {
    let result = [...loans];

    // Apply search query
    if (query) {
      result = result.filter(loan =>
        loan.name.toLowerCase().includes(query.toLowerCase()) ||
        loan.lender?.toLowerCase().includes(query.toLowerCase())
      );
    }

    // Apply status filter
    if (filter === 'active') {
      result = result.filter(loan => loan.paymentStatus !== PAYMENT_STATUS.COMPLETED);
    } else if (filter === 'completed') {
      result = result.filter(loan => loan.paymentStatus === PAYMENT_STATUS.COMPLETED);
    } else if (filter === 'overdue') {
      result = result.filter(loan => loan.paymentStatus === PAYMENT_STATUS.OVERDUE);
    } else if (filter === 'due_soon') {
      result = result.filter(loan => loan.paymentStatus === PAYMENT_STATUS.DUE_SOON);
    }

    // Apply loan type filter
    if (loanType !== 'all') {
      result = result.filter(loan => loan.type === loanType);
    }

    // Apply sorting
    result.sort((a, b) => {
      let valueA = a[sort];
      let valueB = b[sort];

      // Handle special cases
      if (sort === 'name' || sort === 'lender') {
        valueA = (valueA || '').toLowerCase();
        valueB = (valueB || '').toLowerCase();
      } else if (sort === 'next_payment_date' || sort === 'start_date' || sort === 'end_date') {
        valueA = valueA ? new Date(valueA).getTime() : 0;
        valueB = valueB ? new Date(valueB).getTime() : 0;
      } else {
        valueA = valueA || 0;
        valueB = valueB || 0;
      }

      if (valueA < valueB) return order === 'asc' ? -1 : 1;
      if (valueA > valueB) return order === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredLoans(result);
  };

  useEffect(() => {
    applyFilters(loans, searchQuery, filterType, filterLoanType, sortBy, sortOrder);
  }, [loans, searchQuery, filterType, filterLoanType, sortBy, sortOrder]);

  // Go back to the first page when the search, filters or sort change
  useEffect(() => {
    setPage(0);
  }, [searchQuery, filterType, filterLoanType, sortBy, sortOrder]);

  // Keep the current page in range when the list shrinks (e.g. a loan is deleted or completed)
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredLoans.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [filteredLoans.length, rowsPerPage, page]);

  const handleEditLoan = (loan) => {
    setSelectedLoan({
      ...loan,
      start_date: loan.start_date ? parseDateOnly(loan.start_date) : null,
      end_date: loan.end_date ? parseDateOnly(loan.end_date) : null,
      next_payment_date: loan.next_payment_date ? parseDateOnly(loan.next_payment_date) : null,
      settlement_type: loan.settlement_type || LOAN_SETTLEMENT_TYPES.EMI,
      due_date: loan.due_date ? parseDateOnly(loan.due_date) : null
    });
    setDialogOpen(true);
  };

  const handleSaveLoan = async () => {
    // Prevent double submission
    if (loading) return;
    try {
      setLoading(true);

      // Validate required fields
      const principalValue = parseFloat(selectedLoan.principal_amount);
      const rateValue = parseFloat(selectedLoan.interest_rate);
      if (!selectedLoan.name?.trim()) {
        throw new Error('Loan name is required');
      }
      if (isNaN(principalValue) || principalValue <= 0) {
        throw new Error('Principal amount must be greater than zero');
      }
      if (isNaN(rateValue) || rateValue < 0 || rateValue > 100) {
        throw new Error('Interest rate must be between 0 and 100');
      }
      if (!selectedLoan.start_date || isNaN(new Date(selectedLoan.start_date).getTime())) {
        throw new Error('Please enter a valid start date');
      }

      // Calculate tenure based on settlement type
      let tenure;
      if (selectedLoan.settlement_type === LOAN_SETTLEMENT_TYPES.ONE_TIME) {
        if (!selectedLoan.due_date || toDateString(selectedLoan.due_date) <= toDateString(selectedLoan.start_date)) {
          throw new Error('Due date must be after start date');
        }
        // Less than one calendar month still counts as a 1-month tenure
        tenure = Math.max(1, calculateMonthsBetweenDates(selectedLoan.start_date, selectedLoan.due_date));
      } else {
        tenure = parseInt(selectedLoan.tenure_months);
        if (isNaN(tenure) || tenure <= 0) {
          throw new Error('Tenure must be at least 1 month');
        }
        if (isNaN(parseFloat(selectedLoan.emi_amount)) || parseFloat(selectedLoan.emi_amount) <= 0) {
          throw new Error('EMI amount must be greater than zero');
        }
      }

      // Calculate total interest at end for ONE_TIME settlement loans
      let totalInterestAtEnd = 0;
      let finalAmountAtEnd = parseFloat(selectedLoan.principal_amount);

      if (selectedLoan.settlement_type === LOAN_SETTLEMENT_TYPES.ONE_TIME) {
        const startDate = new Date(selectedLoan.start_date);
        const dueDate = new Date(selectedLoan.due_date);

        // Calculate the number of days between start and due date
        const days = Math.floor((dueDate - startDate) / (1000 * 60 * 60 * 24));
        const numQuarters = days / 90; // Convert days to quarters (90 days per quarter)

        // Convert annual rate to quarterly
        const annualRate = parseFloat(selectedLoan.interest_rate) / 100;
        const quarterlyRate = annualRate / 4;

        // Calculate interest using continuous quarterly compounding
        // For partial quarters, we'll use the exact time fraction
        // A = P * (1 + r)^(t) where t is the exact fraction of quarters
        const finalAmount = parseFloat(selectedLoan.principal_amount) *
          Math.pow(1 + quarterlyRate, numQuarters);

        totalInterestAtEnd = finalAmount - parseFloat(selectedLoan.principal_amount);
        finalAmountAtEnd = finalAmount;
      }

      const updatedLoan = {
        name: selectedLoan.name,
        type: selectedLoan.type,
        principal_amount: parseFloat(selectedLoan.principal_amount),
        interest_rate: parseFloat(selectedLoan.interest_rate),
        tenure_months: tenure,
        emi_amount: parseFloat(selectedLoan.emi_amount),
        start_date: toDateString(selectedLoan.start_date),
        end_date: toDateString(selectedLoan.end_date),
        next_payment_date: toDateString(selectedLoan.next_payment_date),
        lender: selectedLoan.lender,
        description: selectedLoan.description,
        settlement_type: selectedLoan.settlement_type,
        interest_type: selectedLoan.interest_type,
        due_date: selectedLoan.settlement_type === LOAN_SETTLEMENT_TYPES.ONE_TIME ?
          toDateString(selectedLoan.due_date) :
          null
      };

      // Update loan in database
      const { error } = await supabase
        .from('loans')
        .update(updatedLoan)
        .eq('id', selectedLoan.id);

      if (error) throw error;

      // Show success message
      setStatusSnackbar({
        open: true,
        message: 'Loan updated successfully!',
        severity: 'success'
      });

      // Refresh loans and close dialog
      await fetchLoans();
      setDialogOpen(false);
    } catch (error) {
      console.error('Error updating loan:', error);
      setStatusSnackbar({
        open: true,
        message: `Failed to update loan: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPaymentDialog = (loan) => {
    setSelectedLoanForPayment(loan);
    setPaymentRows([{ amount: loan.emi_amount?.toString() || '', date: new Date(), note: '' }]);
    setPaymentDialogOpen(true);
  };




  const handleShowPaymentHistory = async (loan) => {
    try {
      setLoading(true);

      // Use the payments already attached to the loan object for consistency
      const sortedPayments = (loan.payments || []).sort((a, b) =>
        new Date(b.payment_date) - new Date(a.payment_date)
      );

      setPaymentHistory(sortedPayments);
      setSelectedLoan(loan);
      setPaymentHistoryDialogOpen(true);
    } catch (error) {
      console.error('Error showing payment history:', error);
      setStatusSnackbar({
        open: true,
        message: 'Failed to show payment history. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewLoan = async () => {
    // Prevent double submission
    if (loading) return;
    try {
      // Validate required fields
      if (!newLoan.name?.trim() || !newLoan.principal_amount || newLoan.interest_rate === '' || newLoan.interest_rate == null) {
        throw new Error('Please fill in all required fields');
      }

      if (isNaN(parseFloat(newLoan.principal_amount)) || parseFloat(newLoan.principal_amount) <= 0) {
        throw new Error('Principal amount must be greater than zero');
      }

      if (!newLoan.start_date || isNaN(new Date(newLoan.start_date).getTime())) {
        throw new Error('Please enter a valid start date');
      }

      if (newLoan.settlement_type === LOAN_SETTLEMENT_TYPES.EMI && newLoan.tenure_months &&
        (!(parseInt(newLoan.tenure_months) > 0) || !(parseFloat(newLoan.emi_amount) > 0))) {
        throw new Error('Tenure must be at least 1 month and EMI amount must be greater than zero');
      }

      // Additional validation for EMI loans
      if (newLoan.settlement_type === LOAN_SETTLEMENT_TYPES.EMI && !newLoan.tenure_months) {
        throw new Error('Please fill in all required fields for EMI loan');
      }

      // Additional validation for one-time loans
      if (newLoan.settlement_type === LOAN_SETTLEMENT_TYPES.ONE_TIME && !newLoan.due_date) {
        throw new Error('Please select a due date for one-time settlement loan');
      }

      setLoading(true);

      // Get user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      // Format and validate numeric values
      const principal = parseFloat(newLoan.principal_amount);
      const interest = parseFloat(newLoan.interest_rate);

      // Calculate tenure based on settlement type
      let tenure;
      if (newLoan.settlement_type === LOAN_SETTLEMENT_TYPES.ONE_TIME) {
        if (toDateString(newLoan.due_date) <= toDateString(newLoan.start_date)) {
          throw new Error('Due date must be after start date');
        }
        // Less than one calendar month still counts as a 1-month tenure
        tenure = Math.max(1, calculateMonthsBetweenDates(newLoan.start_date, newLoan.due_date));
      } else {
        tenure = parseInt(newLoan.tenure_months);
      }

      const emi = newLoan.settlement_type === LOAN_SETTLEMENT_TYPES.EMI ?
        parseFloat(newLoan.emi_amount || '0') : principal;

      // Validate interest rate
      if (interest < 0 || interest > 100) {
        throw new Error('Interest rate must be between 0 and 100');
      }

      // Validate other numeric values
      if (isNaN(principal) || isNaN(interest) || isNaN(tenure)) {
        throw new Error('Invalid numeric values');
      }

      // Format dates and prepare loan data
      const loanData = {
        name: newLoan.name.trim(),
        type: newLoan.type || 'personal',
        user_id: user.id,
        principal_amount: principal,
        interest_rate: interest,
        tenure_months: tenure,
        emi_amount: emi,
        remaining_amount: principal,
        start_date: toDateString(newLoan.start_date),
        end_date: toDateString(newLoan.end_date),
        next_payment_date: newLoan.settlement_type === LOAN_SETTLEMENT_TYPES.EMI ?
          toDateString(newLoan.next_payment_date) :
          toDateString(newLoan.due_date),
        lender: newLoan.lender?.trim() || null,
        description: newLoan.description?.trim() || null,
        payment_day: newLoan.payment_day || null,
        settlement_type: newLoan.settlement_type,
        interest_type: newLoan.interest_type,
        due_date: toDateString(newLoan.due_date)
      };

      // Create the loan
      const { error } = await supabase
        .from('loans')
        .insert(loanData);

      if (error) throw error;

      // Show success message
      setStatusSnackbar({
        open: true,
        message: 'New loan created successfully!',
        severity: 'success'
      });

      // Reset form and close dialog
      setNewLoan({
        name: '',
        type: 'personal',
        principal_amount: '',
        interest_rate: '',
        tenure_months: '',
        emi_amount: '',
        start_date: new Date(),
        end_date: '',
        lender: '',
        remaining_amount: '',
        next_payment_date: new Date(),
        payment_day: '',
        description: '',
        settlement_type: LOAN_SETTLEMENT_TYPES.EMI,
        interest_type: 'diminishing',
        due_date: null
      });

      // Refresh loans and close dialog
      await fetchLoans();
      setNewLoanDialogOpen(false);
    } catch (error) {
      console.error('Error creating loan:', error);
      setStatusSnackbar({
        open: true,
        message: `Failed to create loan: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setStatusSnackbar({
      ...statusSnackbar,
      open: false
    });
  };

  const getStatusChip = (status, daysUntilDue) => {
    switch (status) {
      case PAYMENT_STATUS.COMPLETED:
        return <Chip label="Completed" color="success" size="small" />;
      case PAYMENT_STATUS.OVERDUE:
        return <Chip
          label={`Overdue by ${Math.abs(daysUntilDue)} days`}
          color="error"
          size="small"
        />;
      case PAYMENT_STATUS.DUE_SOON:
        return <Chip
          label={`Due in ${daysUntilDue} days`}
          color="warning"
          size="small"
        />;
      case PAYMENT_STATUS.UPCOMING:
        return <Chip
          label={`Due in ${daysUntilDue} days`}
          color="info"
          size="small"
        />;
      default:
        return <Chip label="Unknown" color="default" size="small" />;
    }
  };

  useEffect(() => {
    if (newLoan.principal_amount && newLoan.interest_rate && newLoan.tenure_months) {
      const calculatedEMI = calculateEMI(
        newLoan.principal_amount,
        newLoan.interest_rate,
        newLoan.tenure_months
      );
      setNewLoan(prev => ({
        ...prev,
        emi_amount: calculatedEMI.toString()
      }));
    }
  }, [newLoan.principal_amount, newLoan.interest_rate, newLoan.tenure_months]);

  // Add this effect to auto-calculate end date in the New Loan form
  useEffect(() => {
    if (newLoan.start_date && newLoan.tenure_months) {
      const calculatedEndDate = calculateEndDate(newLoan.start_date, newLoan.tenure_months);
      setNewLoan(prev => ({
        ...prev,
        end_date: calculatedEndDate
      }));
    }
  }, [newLoan.start_date, newLoan.tenure_months]);

  // Add this effect to calculate end date when editing a loan
  useEffect(() => {
    if (selectedLoan?.start_date && selectedLoan?.tenure_months) {
      const calculatedEndDate = calculateEndDate(selectedLoan.start_date, selectedLoan.tenure_months);
      setSelectedLoan(prev => ({
        ...prev,
        end_date: calculatedEndDate
      }));
    }
  }, [selectedLoan?.start_date, selectedLoan?.tenure_months]);

  // Add these new handler functions
  const handleEditPayment = (payment) => {
    setEditingPayment({
      ...payment,
      payment_date: parseDateOnly(payment.payment_date)
    });
    setEditPaymentDialogOpen(true);
  };

  const handleDeletePayment = (payment) => {
    setPaymentToDelete(payment);
    setDeletePaymentDialogOpen(true);
  };

  const confirmDeletePayment = async () => {
    // Prevent double submission
    if (loading) return;
    try {
      setLoading(true);

      if (!paymentToDelete) return;

      // Delete the payment
      const { error: deleteError } = await supabase
        .from('loan_payments')
        .delete()
        .eq('id', paymentToDelete.id);

      if (deleteError) throw deleteError;

      // Update loan's remaining amount and next payment date for EMI loans
      const updatedRemainingAmount = selectedLoan.remaining_amount + paymentToDelete.amount;

      let updateData = { remaining_amount: updatedRemainingAmount };

      // Recalculate next payment date for EMI loans
      if (selectedLoan.settlement_type === LOAN_SETTLEMENT_TYPES.EMI) {
        // EMI instalments left after removing this payment (advance payments don't count)
        const remainingPayments = countEmiPayments(
          (selectedLoan.payments || []).filter(p => p.id !== paymentToDelete.id)
        );
        const newNextPayment = addMonths(parseDateOnly(selectedLoan.start_date), remainingPayments + 1);

        updateData.next_payment_date = toDateString(newNextPayment);
      }

      const { error: updateError } = await supabase
        .from('loans')
        .update(updateData)
        .eq('id', selectedLoan.id);

      if (updateError) throw updateError;

      // Fetch fresh payment history for this loan
      const { data: freshPayments, error: paymentsError } = await supabase
        .from('loan_payments')
        .select('*')
        .eq('loan_id', selectedLoan.id)
        .order('payment_date', { ascending: false });

      if (paymentsError) throw paymentsError;

      // Update payment history display
      setPaymentHistory(freshPayments || []);

      // Update selectedLoan with fresh payments
      setSelectedLoan(prev => ({
        ...prev,
        payments: freshPayments || []
      }));

      // Close dialog and show success message
      setDeletePaymentDialogOpen(false);
      setPaymentToDelete(null);

      setStatusSnackbar({
        open: true,
        message: 'Payment deleted successfully!',
        severity: 'success'
      });

      // Refresh loans list in background
      await fetchLoans();
    } catch (error) {
      console.error('Error deleting payment:', error);
      setStatusSnackbar({
        open: true,
        message: `Failed to delete payment: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePaymentEdit = async () => {
    // Prevent double submission
    if (loading) return;
    try {
      setLoading(true);

      // Validate the new amount
      const newAmount = parseFloat(editingPayment.amount);
      if (isNaN(newAmount) || newAmount <= 0) {
        throw new Error('Invalid payment amount');
      }
      if (!editingPayment.payment_date || isNaN(new Date(editingPayment.payment_date).getTime())) {
        throw new Error('Please enter a valid payment date');
      }

      // Find the original payment
      const originalPayment = paymentHistory.find(p => p.id === editingPayment.id);
      if (!originalPayment) {
        throw new Error('Original payment not found');
      }

      // Calculate the difference in amounts
      const amountDifference = newAmount - originalPayment.amount;

      // Update the payment record
      const { error: updateError } = await supabase
        .from('loan_payments')
        .update({
          amount: newAmount,
          payment_date: toDateString(editingPayment.payment_date),
          description: editingPayment.description
        })
        .eq('id', editingPayment.id);

      if (updateError) throw updateError;

      // Update loan's remaining amount
      // If new amount is higher, decrease remaining amount
      // If new amount is lower, increase remaining amount
      const newRemainingAmount = selectedLoan.remaining_amount + (-amountDifference);

      const { error: loanUpdateError } = await supabase
        .from('loans')
        .update({
          remaining_amount: Math.max(0, newRemainingAmount)
        })
        .eq('id', selectedLoan.id);

      if (loanUpdateError) throw loanUpdateError;

      // Fetch fresh payment history for this loan
      const { data: freshPayments, error: paymentsError } = await supabase
        .from('loan_payments')
        .select('*')
        .eq('loan_id', selectedLoan.id)
        .order('payment_date', { ascending: false });

      if (paymentsError) throw paymentsError;

      // Update payment history display
      setPaymentHistory(freshPayments || []);

      // Update selectedLoan with fresh payments
      setSelectedLoan(prev => ({
        ...prev,
        payments: freshPayments || []
      }));

      // Close dialog and show success message
      setEditPaymentDialogOpen(false);

      setStatusSnackbar({
        open: true,
        message: 'Payment updated successfully!',
        severity: 'success'
      });

      // Refresh loans list in background
      await fetchLoans();

    } catch (error) {
      console.error('Error updating payment:', error);
      setStatusSnackbar({
        open: true,
        message: `Failed to update payment: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Add this effect for EMI calculation in edit mode.
  // Only recalculate after the user changes principal / rate / tenure (emiInputsChanged), so that
  // opening the dialog does not overwrite the stored EMI (e.g. one reduced by an advance payment).
  useEffect(() => {
    if (selectedLoan?.emiInputsChanged && selectedLoan?.settlement_type === LOAN_SETTLEMENT_TYPES.EMI &&
      selectedLoan?.principal_amount && selectedLoan?.interest_rate !== '' && selectedLoan?.interest_rate != null && selectedLoan?.tenure_months) {
      const calculatedEMI = calculateEMI(
        selectedLoan.principal_amount,
        selectedLoan.interest_rate,
        selectedLoan.tenure_months
      );
      setSelectedLoan(prev => ({
        ...prev,
        emi_amount: calculatedEMI.toString()
      }));
    }
  }, [selectedLoan?.emiInputsChanged, selectedLoan?.settlement_type, selectedLoan?.principal_amount, selectedLoan?.interest_rate, selectedLoan?.tenure_months]);

  // Update End Date calculation
  useEffect(() => {
    if (selectedLoan?.start_date && selectedLoan?.tenure_months) {
      const calculatedEndDate = calculateEndDate(selectedLoan.start_date, selectedLoan.tenure_months);
      setSelectedLoan(prev => ({
        ...prev,
        end_date: calculatedEndDate
      }));
    }
  }, [selectedLoan?.start_date, selectedLoan?.tenure_months]);

  // Add delete loan handler function
  const handleDeleteLoan = async () => {
    // Prevent double submission
    if (loading) return;
    try {
      setLoading(true);

      // First delete all payments associated with the loan
      const { error: paymentsError } = await supabase
        .from('loan_payments')
        .delete()
        .eq('loan_id', loanToDelete.id);

      if (paymentsError) throw paymentsError;

      // Then delete the loan
      const { error: loanError } = await supabase
        .from('loans')
        .delete()
        .eq('id', loanToDelete.id);

      if (loanError) throw loanError;

      // Show success message
      setStatusSnackbar({
        open: true,
        message: 'Loan deleted successfully!',
        severity: 'success'
      });

      // Refresh loans list
      await fetchLoans();
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting loan:', error);
      setStatusSnackbar({
        open: true,
        message: `Failed to delete loan: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Add delete confirmation dialog
  const DeleteConfirmationDialog = () => (
    <Dialog
      open={deleteDialogOpen}
      onClose={() => setDeleteDialogOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: { xs: 1, sm: 2 }
        }
      }}
    >
      <DialogTitle sx={{
        pb: 1,
        fontSize: { xs: '1.25rem', sm: '1.5rem' },
        fontWeight: 600,
        color: 'error.main'
      }}>
        Delete Loan
      </DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to delete the loan "{loanToDelete?.name}"? This action cannot be undone and will also delete all associated payment records.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button
          onClick={() => setDeleteDialogOpen(false)}
          color="inherit"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleDeleteLoan}
          color="error"
          variant="contained"
          disabled={loading}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Add delete payment confirmation dialog
  const DeletePaymentConfirmationDialog = () => (
    <Dialog
      open={deletePaymentDialogOpen}
      onClose={() => setDeletePaymentDialogOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: { xs: 1, sm: 2 }
        }
      }}
    >
      <DialogTitle sx={{
        pb: 1,
        fontSize: { xs: '1.25rem', sm: '1.5rem' },
        fontWeight: 600,
        color: 'error.main'
      }}>
        Delete Payment
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Are you sure you want to delete this payment?
        </Typography>
        {paymentToDelete && (
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Amount:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatCurrency(paymentToDelete.amount)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Date:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatDateToDDMMYYYY(paymentToDelete.payment_date)}
                </Typography>
              </Grid>
              {paymentToDelete.description && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Description:
                  </Typography>
                  <Typography variant="body2">
                    {paymentToDelete.description}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}
        <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
          ⚠️ This action cannot be undone. The loan balance will be adjusted accordingly.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button
          onClick={() => {
            setDeletePaymentDialogOpen(false);
            setPaymentToDelete(null);
          }}
          color="inherit"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={confirmDeletePayment}
          color="error"
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Deleting...' : 'Delete Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading && loans.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && loans.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  // Calculate new EMI or tenure after advance payment
  const calculateAdvancePaymentImpact = (loan, advanceAmount, option) => {
    const monthlyRate = loan.interest_rate / (12 * 100);
    const currentEMI = parseFloat(loan.emi_amount);
    const paymentsMade = countEmiPayments(loan.payments);
    
    // Calculate actual remaining PRINCIPAL (not including accrued interest)
    // loan.remaining_amount = (principal + accrued interest) - total paid
    // We need: principal - principal paid
    const remainingPrincipal = loan.principal_amount - (loan.principalPaid || 0);
    const remainingTenure = loan.tenure_months - paymentsMade;

    // Validate inputs
    if (remainingTenure <= 0 || remainingPrincipal <= 0 || currentEMI <= 0) {
      return {
        newEMI: 0,
        newTenure: 0,
        remainingTenure: 0,
        newEndDate: null,
        savings: 0
      };
    }

    // Helper function to calculate total interest for a given principal, EMI, and tenure
    const calculateTotalInterest = (principal, emi, tenure) => {
      if (principal <= 0 || emi <= 0 || tenure <= 0) return 0;
      
      let balance = principal;
      let totalInterest = 0;
      
      for (let i = 0; i < tenure; i++) {
        const interestForMonth = balance * monthlyRate;
        const principalForMonth = emi - interestForMonth;
        
        // If EMI is less than interest, loan will never be paid off
        if (principalForMonth <= 0) {
          console.warn('EMI is too low to cover interest');
          return totalInterest;
        }
        
        totalInterest += interestForMonth;
        balance -= principalForMonth;
        
        // Break if loan is paid off
        if (balance <= 0) break;
      }
      
      return totalInterest;
    };

    // Calculate current scenario total interest
    const currentTotalInterest = calculateTotalInterest(remainingPrincipal, currentEMI, remainingTenure);

    // New principal after advance payment
    const newPrincipal = Math.max(0, remainingPrincipal - advanceAmount);

    console.log('Advance Payment Calculation:', {
      remainingPrincipal,
      advanceAmount,
      newPrincipal,
      currentEMI,
      remainingTenure,
      monthlyRate: monthlyRate * 100 + '%',
      currentTotalInterest
    });

    if (option === 'reduce_emi') {
      // Keep tenure same, calculate new EMI
      const newEMI = newPrincipal > 0
        ? (monthlyRate === 0
          ? newPrincipal / remainingTenure
          : (newPrincipal * monthlyRate * Math.pow(1 + monthlyRate, remainingTenure)) /
            (Math.pow(1 + monthlyRate, remainingTenure) - 1))
        : 0;

      const newEndDate = addMonths(parseDateOnly(loan.start_date), paymentsMade + remainingTenure);

      // Calculate new scenario total interest
      const newTotalInterest = calculateTotalInterest(newPrincipal, newEMI, remainingTenure);

      // Interest savings = current interest - new interest
      const interestSavings = currentTotalInterest - newTotalInterest;

      console.log('Reduce EMI Result:', {
        newEMI: Math.round(newEMI),
        newTotalInterest,
        interestSavings
      });

      return {
        newEMI: Math.round(newEMI),
        newTenure: loan.tenure_months,
        remainingTenure: remainingTenure,
        newEndDate: newEndDate,
        savings: Math.max(0, Math.round(interestSavings))
      };
    } else {
      // Keep EMI same, calculate new tenure
      let newTenure = 0;
      if (newPrincipal > 0 && currentEMI > 0) {
        // Calculate number of months needed with current EMI
        newTenure = monthlyRate === 0
          ? Math.ceil(newPrincipal / currentEMI)
          : currentEMI > newPrincipal * monthlyRate
            ? Math.ceil(
              Math.log(currentEMI / (currentEMI - newPrincipal * monthlyRate)) /
              Math.log(1 + monthlyRate)
            )
            : remainingTenure; // EMI does not cover interest: tenure cannot be reduced
      }

      const newEndDate = addMonths(parseDateOnly(loan.start_date), paymentsMade + newTenure);

      // Calculate new scenario total interest
      const newTotalInterest = calculateTotalInterest(newPrincipal, currentEMI, newTenure);

      // Interest savings = current interest - new interest
      const interestSavings = currentTotalInterest - newTotalInterest;

      console.log('Reduce Tenure Result:', {
        newTenure,
        newTotalInterest,
        interestSavings
      });

      return {
        newEMI: currentEMI,
        newTenure: paymentsMade + newTenure,
        remainingTenure: newTenure,
        newEndDate: newEndDate,
        savings: Math.max(0, Math.round(interestSavings))
      };
    }
  };

  // Handle opening advance payment dialog
  const handleOpenAdvancePayment = (loan) => {
    setAdvancePaymentData({
      loan: loan,
      amount: '',
      option: 'reduce_emi',
      newEMI: 0,
      newTenure: 0,
      newEndDate: null,
      savings: 0
    });
    setAdvancePaymentDialogOpen(true);
  };

  // Handle advance payment amount change
  const handleAdvancePaymentChange = (amount, option) => {
    if (!advancePaymentData.loan || !amount || parseFloat(amount) <= 0) {
      setAdvancePaymentData(prev => ({
        ...prev,
        amount: amount,
        option: option,
        newEMI: 0,
        newTenure: 0,
        newEndDate: null,
        savings: 0
      }));
      return;
    }

    const impact = calculateAdvancePaymentImpact(
      advancePaymentData.loan,
      parseFloat(amount),
      option
    );

    setAdvancePaymentData(prev => ({
      ...prev,
      amount: amount,
      option: option,
      ...impact
    }));
  };

  // Handle submitting advance payment
  const handleSubmitAdvancePayment = async () => {
    // Prevent multiple submissions
    if (loading) return;
    
    try {
      setLoading(true);
      const { loan, amount, option, newEMI, newTenure } = advancePaymentData;

      if (!loan || !amount || parseFloat(amount) <= 0) {
        throw new Error('Please enter a valid advance payment amount');
      }
      if (parseFloat(amount) > (loan.remaining_amount || 0)) {
        throw new Error(`Advance payment cannot exceed the remaining amount (${formatCurrency(loan.remaining_amount || 0)})`);
      }
      // Validate the recalculated schedule before writing anything, so a failure
      // can't leave a recorded payment without the matching loan update
      if (!advancePaymentData.newEndDate || isNaN(advancePaymentData.newEndDate.getTime()) ||
        !Number.isFinite(newEMI) || !Number.isFinite(newTenure)) {
        throw new Error('Could not recalculate the loan schedule for this advance payment');
      }

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      // Record the payment - single insert
      const { error: paymentError } = await supabase
        .from('loan_payments')
        .insert([{
          loan_id: loan.id,
          user_id: user.id,
          amount: parseFloat(amount),
          payment_date: toDateString(new Date()),
          description: `Advance payment - ${option === 'reduce_emi' ? 'EMI reduced' : 'Tenure reduced'}`,
          payment_type: 'advance',
          created_at: new Date().toISOString()
        }]);

      if (paymentError) throw paymentError;

      // Update loan details based on option
      const updateData = {
        emi_amount: option === 'reduce_emi' ? newEMI : loan.emi_amount,
        tenure_months: option === 'reduce_tenure' ? newTenure : loan.tenure_months,
        end_date: toDateString(advancePaymentData.newEndDate)
      };

      const { error: updateError } = await supabase
        .from('loans')
        .update(updateData)
        .eq('id', loan.id);

      if (updateError) throw updateError;

      // Close dialog first to prevent re-renders
      setAdvancePaymentDialogOpen(false);

      setStatusSnackbar({
        open: true,
        message: `Advance payment recorded successfully! ${
          option === 'reduce_emi' 
            ? `New EMI: ${formatCurrency(newEMI)}` 
            : `New tenure: ${newTenure} months`
        }`,
        severity: 'success'
      });

      // Refresh loans data
      await fetchLoans();
    } catch (error) {
      console.error('Error processing advance payment:', error);
      setStatusSnackbar({
        open: true,
        message: `Failed to process advance payment: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleShowAmortizationDetails = (loan) => {
    const monthlyRate = loan.interest_rate / (12 * 100);
    const emi = parseFloat(loan.emi_amount) || 0;
    const principal = parseFloat(loan.principal_amount) || 0;
    const numPayments = parseInt(loan.tenure_months) || 0;

    let schedule = [];
    let remainingPrincipal = principal;
    let totalInterestPaid = 0;
    let totalPrincipalPaid = 0;

    for (let i = 1; i <= numPayments && remainingPrincipal > 0.005; i++) {
      const interestPayment = remainingPrincipal * monthlyRate;
      let principalPayment = emi - interestPayment;
      // Final instalment (or an EMI larger than the balance) clears the balance exactly,
      // absorbing the rounding of the EMI (only small residuals; a real shortfall stays visible)
      if ((i === numPayments && Math.abs(remainingPrincipal - principalPayment) < emi) ||
        principalPayment > remainingPrincipal) {
        principalPayment = remainingPrincipal;
      }
      const rowEmi = principalPayment + interestPayment;
      remainingPrincipal -= principalPayment;
      totalInterestPaid += interestPayment;
      totalPrincipalPaid += principalPayment;

      schedule.push({
        month: i,
        emi: rowEmi,
        principalPayment: principalPayment,
        interestPayment: interestPayment,
        totalPrincipalPaid: totalPrincipalPaid,
        totalInterestPaid: totalInterestPaid,
        remainingPrincipal: Math.max(0, remainingPrincipal)
      });
    }

    setAmortizationSchedule(schedule);
    setAmortizationDialogOpen(true);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{}}>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Loans
            </Typography>
            <TourButton onClick={loansTour.start} />
          </Box>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 2, sm: 0 },
            mb: { xs: 2, sm: 4 }
          }}>
            <Button
              data-tour="loans-add"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setNewLoanDialogOpen(true)}
              sx={{
                background: 'linear-gradient(45deg, #1976d2, #1565c0)',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(45deg, #1565c0, #0d47a1)',
                  boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              Add New Loan
            </Button>
          </Box>

          <Paper data-tour="loans-filters" sx={{
            p: 3,
            mb: 4,
            bgcolor: 'background.paper',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            borderRadius: 3,
            boxShadow: 'var(--fx-card-shadow)'
          }}>
            <Typography variant="h6" sx={{
              mb: 3,
              fontWeight: 600,
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Box sx={{
                width: 3,
                height: 18,
                background: 'linear-gradient(45deg, #1976d2, #1565c0)',
                borderRadius: 1
              }} />
              Search & Filter
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Search Loans"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(255, 255, 255, 0.8)',
                      borderRadius: 2,
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.9)',
                      },
                      '&.Mui-focused': {
                        background: 'rgba(255, 255, 255, 1)',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterType}
                    label="Status"
                    onChange={(e) => setFilterType(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: 2,
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.9)',
                        },
                        '&.Mui-focused': {
                          background: 'rgba(255, 255, 255, 1)',
                        }
                      }
                    }}
                  >
                    <MenuItem value="active">Active Loans</MenuItem>
                    <MenuItem value="overdue">Overdue Payments</MenuItem>
                    <MenuItem value="due_soon">Due Soon</MenuItem>
                    <MenuItem value="completed">Completed Loans</MenuItem>
                    <MenuItem value="all">All Status</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Loan Type</InputLabel>
                  <Select
                    value={filterLoanType}
                    label="Loan Type"
                    onChange={(e) => setFilterLoanType(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: 2,
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.9)',
                        },
                        '&.Mui-focused': {
                          background: 'rgba(255, 255, 255, 1)',
                        }
                      }
                    }}
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    {loanTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={10} sm={5} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={sortBy}
                    label="Sort By"
                    onChange={(e) => setSortBy(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: 2,
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.9)',
                        },
                        '&.Mui-focused': {
                          background: 'rgba(255, 255, 255, 1)',
                        }
                      }
                    }}
                  >
                    {sortOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={2} sm={1} md={1}>
                <IconButton
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  sx={{
                    background: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: 2,
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 1)',
                      transform: 'scale(1.05)'
                    }
                  }}
                >
                  {sortOrder === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                </IconButton>
              </Grid>
            </Grid>
          </Paper>

          <Grid container spacing={2}>
            {loading && filteredLoans.length === 0 ? (
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
              </Grid>
            ) : error ? (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            ) : filteredLoans.length === 0 ? (
              <Grid item xs={12}>
                <Alert severity="info">
                  {loans.length > 0
                    ? 'No loans match your search criteria.'
                    : 'You have no loans yet. Click "Add New Loan" to get started.'}
                </Alert>
              </Grid>
            ) : (
              <>
                {(rowsPerPage > 0
                  ? filteredLoans.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : filteredLoans
                ).map((loan) => (
                  <Grid item xs={12} key={loan.id}>
                    <Card
                      data-tour="loans-card"
                      elevation={2}
                      sx={{
                        borderRadius: 3,
                        bgcolor: 'background.paper',
                        border: loan.paymentStatus === 'overdue' ? '2px solid #f44336' : '1px solid rgba(255, 255, 255, 0.8)',
                        boxShadow: 'var(--fx-card-shadow)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          boxShadow: 'var(--fx-card-shadow-hover)',
                        },
                        height: '100%'
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Grid container spacing={4}>
                          <Grid item xs={12} md={3}>
                            <Box>
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 700,
                                  color: 'text.primary',
                                  mb: 1
                                }}
                              >
                                {loan.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {getLoanTypeLabel(loan.type)}
                                {loan.lender && ` • ${loan.lender}`}
                              </Typography>
                              <Box component="span" data-tour="loans-status" sx={{ display: 'inline-flex' }}>
                                {getStatusChip(loan.paymentStatus, loan.daysUntilDue)}
                              </Box>
                              {loan.end_date && (
                                <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                                  End Date: {formatDateToDDMMYYYY(loan.end_date)}
                                </Typography>
                              )}
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={3}>
                            <Box data-tour="loans-amounts" sx={{
                              p: 2,
                              borderRadius: 2,
                              background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.05), rgba(33, 150, 243, 0.02))',
                              border: '1px solid rgba(33, 150, 243, 0.1)',
                              minHeight: 280
                            }}>
                              <Typography variant="subtitle2" sx={{
                                mb: 2,
                                fontWeight: 600,
                                color: '#2c3e50'
                              }}>
                                Principal & Interest
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  p: 1,
                                  borderRadius: 1,
                                  background: 'rgba(255, 255, 255, 0.5)'
                                }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    Principal:
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                    {formatCurrency(loan.principal_amount)}
                                  </Typography>
                                </Box>
                                <Box sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  p: 1,
                                  borderRadius: 1,
                                  background: 'rgba(255, 255, 255, 0.5)'
                                }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    Interest at End:
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>
                                    {formatCurrency(loan.totalInterestAtEnd)}
                                  </Typography>
                                </Box>
                                <Box sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  p: 1,
                                  borderRadius: 1,
                                  background: 'rgba(255, 255, 255, 0.5)'
                                }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    Total at End:
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 600 }}>
                                    {formatCurrency(loan.finalAmountAtEnd)}
                                  </Typography>
                                </Box>

                                {/* Total Paid */}
                                <Box sx={{
                                  textAlign: 'center',
                                  p: 1.5,
                                  borderRadius: 2,
                                  background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1), rgba(33, 150, 243, 0.05))',
                                  border: '1px solid rgba(33, 150, 243, 0.2)',
                                  mt: 1
                                }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    Total Paid
                                  </Typography>
                                  <Typography variant="body1" sx={{ mt: 0.5, textAlign: 'center' }}>
                                    <span style={{ color: '#1976d2', fontWeight: 700 }}>{formatCurrency(loan.totalPaid)}</span>
                                    <span style={{ color: 'black', margin: '0 4px', fontWeight: 400 }}> of </span>
                                    <span style={{ color: '#1976d2', fontWeight: 700 }}>{formatCurrency(loan.totalAmount)}</span>
                                  </Typography>
                                </Box>

                              </Box>
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={3}>
                            <Box data-tour="loans-payment-details" sx={{
                              p: 2,
                              borderRadius: 2,
                              background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.05), rgba(156, 39, 176, 0.02))',
                              border: '1px solid rgba(156, 39, 176, 0.1)',
                              minHeight: 280
                            }}>
                              <Typography variant="subtitle2" sx={{
                                mb: 2,
                                fontWeight: 600,
                                color: '#2c3e50'
                              }}>
                                Payment Details
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {loan.settlement_type === LOAN_SETTLEMENT_TYPES.EMI ? (
                                  <>
                                    <Box sx={{
                                      textAlign: 'center',
                                      p: 1.5,
                                      borderRadius: 2,
                                      background: 'rgba(255, 255, 255, 0.6)',
                                      border: '1px solid rgba(255, 255, 255, 0.8)'
                                    }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        EMI Amount
                                      </Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 700, color: 'secondary.main', mt: 0.5 }}>
                                        {formatCurrency(loan.emi_amount)}
                                      </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                                      Payment Progress: {countEmiPayments(loan.payments)} Made, {' '}
                                      {Math.max(0, loan.tenure_months - countEmiPayments(loan.payments))} Remaining {' '}
                                      ({loan.tenure_months} Expected)
                                    </Typography>
                                  </>
                                ) : (
                                  <Box sx={{
                                    textAlign: 'center',
                                    p: 1.5,
                                    borderRadius: 2,
                                    background: 'rgba(255, 255, 255, 0.6)',
                                    border: '1px solid rgba(255, 255, 255, 0.8)'
                                  }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                      Due Date
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'error.main', mt: 0.5 }}>
                                      {loan.due_date ? formatDateToDDMMYYYY(loan.due_date) : 'N/A'}
                                    </Typography>
                                  </Box>
                                )}
                                <Box sx={{
                                  textAlign: 'center',
                                  p: 1.5,
                                  borderRadius: 2,
                                  background: 'rgba(255, 255, 255, 0.6)',
                                  border: '1px solid rgba(255, 255, 255, 0.8)',
                                  mt: 1
                                }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    Next Payment
                                  </Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 700, color: 'info.main', mt: 0.5 }}>
                                    {loan.next_payment_date ? formatDateToDDMMYYYY(loan.next_payment_date) : 'N/A'}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={3}>
                            <Box data-tour="loans-progress" sx={{
                              p: 2,
                              borderRadius: 2,
                              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.05), rgba(76, 175, 80, 0.02))',
                              border: '1px solid rgba(76, 175, 80, 0.1)',
                              minHeight: 280
                            }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle2" sx={{
                                  fontWeight: 600,
                                  color: '#2c3e50'
                                }}>
                                  Progress
                                </Typography>
                                <Typography variant="caption" sx={{
                                  fontWeight: 600,
                                  color: '#4caf50',
                                  fontSize: '0.75rem'
                                }}>
                                  {loan.completionPercentage.toFixed(1)}%
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={loan.completionPercentage}
                                sx={{
                                  height: 10,
                                  borderRadius: 5,
                                  mb: 1,
                                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                  boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 5,
                                    background: 'linear-gradient(45deg, #4caf50, #388e3c)'
                                  }
                                }}
                              />

                              <Grid container spacing={1}>
                                <Grid item xs={12}>
                                  <Box sx={{
                                    textAlign: 'center',
                                    p: 1,
                                    borderRadius: 1,
                                    background: 'rgba(255, 255, 255, 0.5)'
                                  }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                      Principal Paid
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                                      {formatCurrency(loan.principalPaid)}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12}>
                                  <Box sx={{
                                    textAlign: 'center',
                                    p: 1,
                                    borderRadius: 1,
                                    background: 'rgba(255, 255, 255, 0.5)'
                                  }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                      Interest Paid
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                                      {formatCurrency(loan.interestPaid)}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12}>
                                  <Box sx={{
                                    textAlign: 'center',
                                    p: 1,
                                    borderRadius: 1,
                                    background: 'rgba(255, 255, 255, 0.5)'
                                  }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                      Remaining
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                                      {formatCurrency(loan.remaining_amount)}
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                      <CardActions
                        data-tour="loans-actions"
                        sx={{
                          flexDirection: 'row',
                          justifyContent: 'flex-end',
                          gap: 1,
                          p: 3,
                          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                          background: 'rgba(248, 249, 250, 0.5)'
                        }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<HistoryIcon />}
                          onClick={() => handleShowPaymentHistory(loan)}
                          size="small"
                          sx={{
                            borderWidth: 2,
                            '&:hover': {
                              borderWidth: 2,
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                            }
                          }}
                        >
                          History
                        </Button>
                        {loan.settlement_type === LOAN_SETTLEMENT_TYPES.EMI && (
                          <Button
                            variant="outlined"
                            color="info"
                            startIcon={<TableChartIcon />}
                            data-tour="loans-amortization"
                            onClick={() => handleShowAmortizationDetails(loan)}
                            size="small"
                            sx={{
                              borderWidth: 2,
                              '&:hover': {
                                borderWidth: 2,
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                              }
                            }}
                          >
                            Amortization
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          startIcon={<PaymentIcon />}
                          data-tour="loans-pay"
                          onClick={() => handleOpenPaymentDialog(loan)}
                          disabled={loan.paymentStatus === PAYMENT_STATUS.COMPLETED}
                          size="small"
                          sx={{
                            background: 'linear-gradient(45deg, #4caf50, #388e3c)',
                            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                            '&:hover': {
                              background: 'linear-gradient(45deg, #388e3c, #2e7d32)',
                              boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
                            }
                          }}
                        >
                          Add Payment
                        </Button>
                        {loan.settlement_type === LOAN_SETTLEMENT_TYPES.EMI && loan.paymentStatus !== PAYMENT_STATUS.COMPLETED && (
                          <Button
                            variant="contained"
                            color="warning"
                            startIcon={<PaymentIcon />}
                            data-tour="loans-advance"
                            onClick={() => handleOpenAdvancePayment(loan)}
                            size="small"
                            sx={{
                              borderWidth: 2,
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
                              }
                            }}
                          >
                            Advance Payment
                          </Button>
                        )}
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<EditIcon />}
                          onClick={() => handleEditLoan(loan)}
                          size="small"
                          sx={{
                            borderWidth: 2,
                            '&:hover': {
                              borderWidth: 2,
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                            }
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => {
                            setLoanToDelete(loan);
                            setDeleteDialogOpen(true);
                          }}
                          size="small"
                          sx={{
                            borderWidth: 2,
                            '&:hover': {
                              borderWidth: 2,
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)'
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </>
            )}
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
            <Pagination
              count={Math.ceil(filteredLoans.length / rowsPerPage)}
              page={page + 1}
              onChange={(event, value) => setPage(value - 1)}
              color="primary"
              size="medium"
              siblingCount={0}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  background: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 1)',
                    transform: 'scale(1.05)'
                  },
                  '&.Mui-selected': {
                    background: 'linear-gradient(45deg, #1976d2, #1565c0)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #1565c0, #0d47a1)',
                    }
                  }
                }
              }}
            />
          </Box>

          <Dialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                p: { xs: 1, sm: 2 },
                m: { xs: 1, sm: 2 }
              }
            }}
          >
            <DialogTitle sx={{
              pb: 1,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              fontWeight: 600,
              color: 'primary.main'
            }}>
              Edit Loan
            </DialogTitle>
            <DialogContent sx={{ pt: '10px !important' }}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Loan Name"
                    value={selectedLoan?.name || ''}
                    onChange={(e) => setSelectedLoan({ ...selectedLoan, name: e.target.value })}
                    size="small"
                    sx={{ backgroundColor: 'background.paper' }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Lender"
                    value={selectedLoan?.lender || ''}
                    onChange={(e) => setSelectedLoan({ ...selectedLoan, lender: e.target.value })}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Loan Type</InputLabel>
                    <Select
                      value={selectedLoan?.type || ''}
                      label="Loan Type"
                      onChange={(e) => setSelectedLoan({ ...selectedLoan, type: e.target.value })}
                    >
                      {loanTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="edit-loan-settlement-type-label">Settlement Type</InputLabel>
                    <Select
                      labelId="edit-loan-settlement-type-label"
                      value={selectedLoan?.settlement_type || LOAN_SETTLEMENT_TYPES.EMI}
                      disabled
                      label="Settlement Type"
                      onChange={(e) => setSelectedLoan({ ...selectedLoan, settlement_type: e.target.value })}
                    >
                      <MenuItem value={LOAN_SETTLEMENT_TYPES.EMI}>EMI Based</MenuItem>
                      <MenuItem value={LOAN_SETTLEMENT_TYPES.ONE_TIME}>One Time Settlement</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <AmountField
                    fullWidth
                    label="Principal Amount"
                    value={selectedLoan?.principal_amount || ''}
                    onChange={(e) => setSelectedLoan({ ...selectedLoan, principal_amount: e.target.value, emiInputsChanged: true })}
                    size="small"
                    type="number"
                    sx={numberInputStyle}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Interest Rate (%)"
                    value={selectedLoan?.interest_rate ?? ''}
                    onChange={(e) => setSelectedLoan({ ...selectedLoan, interest_rate: e.target.value, emiInputsChanged: true })}
                    size="small"
                    type="number"
                    sx={numberInputStyle}
                  />
                </Grid>
                {selectedLoan?.settlement_type === LOAN_SETTLEMENT_TYPES.EMI && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Tenure (Months)"
                        value={selectedLoan?.tenure_months || ''}
                        onChange={(e) => setSelectedLoan({ ...selectedLoan, tenure_months: e.target.value, emiInputsChanged: true })}
                        size="small"
                        type="number"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <AmountField
                        fullWidth
                        label="EMI Amount"
                        value={selectedLoan?.emi_amount || ''}
                        onChange={(e) => setSelectedLoan({ ...selectedLoan, emi_amount: e.target.value })}
                        size="small"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        helperText="Auto-calculated but can be adjusted"
                        sx={numberInputStyle}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Start Date"
                    value={selectedLoan?.start_date || null}
                    onChange={(date) => setSelectedLoan({ ...selectedLoan, start_date: date })}
                    format="dd/MM/yyyy"
                    renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                  />
                </Grid>
                {selectedLoan?.settlement_type === LOAN_SETTLEMENT_TYPES.EMI && (
                  <>
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        label="Next Payment Date"
                        value={selectedLoan?.next_payment_date || null}
                        onChange={(date) => setSelectedLoan({ ...selectedLoan, next_payment_date: date })}
                        format="dd/MM/yyyy"
                        renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        label="End Date"
                        value={selectedLoan?.end_date || null}
                        disabled
                        format="dd/MM/yyyy"
                        renderInput={(params) =>
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            helperText="Auto-calculated based on start date and tenure"
                          />
                        }
                      />
                    </Grid>
                  </>
                )}
                {selectedLoan?.settlement_type === LOAN_SETTLEMENT_TYPES.ONE_TIME && (
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Loan Due Date"
                      value={selectedLoan?.due_date || null}
                      onChange={(date) => setSelectedLoan({ ...selectedLoan, due_date: date })}
                      format="dd/MM/yyyy"
                      renderInput={(params) =>
                        <TextField
                          {...params}
                          fullWidth
                          size="small"
                          required
                          helperText="Select when the loan amount is due"
                        />
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={selectedLoan?.description || ''}
                    onChange={(e) => setSelectedLoan({ ...selectedLoan, description: e.target.value })}
                    size="small"
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, gap: 1 }}>
              <Button
                onClick={() => setDialogOpen(false)}
                color="inherit"
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveLoan}
                color="primary"
                variant="contained"
                disabled={loading}
              >
                Save Changes
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={paymentDialogOpen}
            onClose={() => setPaymentDialogOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                p: { xs: 1, sm: 2 },
                minWidth: { xs: 340, sm: 600, md: 900 },
              }
            }}
          >
            <DialogTitle sx={{
              pb: 1,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              fontWeight: 600,
              color: 'primary.main'
            }}>
              Add Payment
            </DialogTitle>
            <DialogContent sx={{ pt: '20px !important' }}>
              {paymentRows.map((row, idx) => (
                <Grid container spacing={2} alignItems="stretch" key={idx} sx={{ mb: 1 }}>
                  <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <AmountField
                      fullWidth
                      label="Payment Amount"
                      value={row.amount}
                      onChange={(e) => handlePaymentRowChange(idx, 'amount', e.target.value)}
                      size="small"
                      type="number"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        sx: { height: 56 }
                      }}
                      sx={{ backgroundColor: 'background.paper', height: 56, ...numberInputStyle }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <DatePicker
                      label="Payment Date"
                      value={row.date}
                      onChange={(date) => handlePaymentRowChange(idx, 'date', date)}
                      format="dd/MM/yyyy"
                      renderInput={(params) =>
                        <TextField
                          {...params}
                          fullWidth
                          size="small"
                          sx={{ backgroundColor: 'background.paper', height: 56 }}
                          InputProps={{ ...params.InputProps, sx: { height: 56 } }}
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <TextField
                      fullWidth
                      label="Payment Note"
                      value={row.note}
                      onChange={(e) => handlePaymentRowChange(idx, 'note', e.target.value)}
                      size="small"
                      sx={{ backgroundColor: 'background.paper', height: 56 }}
                      inputProps={{ style: { height: 56, padding: '0 14px' } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <IconButton color="primary" onClick={handleAddPaymentRow} size="large">
                      <AddIcon />
                    </IconButton>
                    {paymentRows.length > 1 && (
                      <IconButton color="error" onClick={() => handleRemovePaymentRow(idx)} size="large">
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}
            </DialogContent>
            <DialogActions sx={{ p: 2.5, gap: 1 }}>
              <Button
                onClick={() => setPaymentDialogOpen(false)}
                color="inherit"
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={handleMakeMultiplePayments}
                color="primary"
                variant="contained"
                disabled={loading}
              >
                Add Payment(s)
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={paymentHistoryDialogOpen} onClose={() => setPaymentHistoryDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Payment History</DialogTitle>
            <DialogContent>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentHistory.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>{formatDateToDDMMYYYY(payment.payment_date)}</TableCell>
                        <TableCell>{formatCurrency(payment.amount)}</TableCell>

                        <TableCell>{payment.description}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleEditPayment(payment)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeletePayment(payment)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPaymentHistoryDialogOpen(false)} color="secondary">
                Close
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={newLoanDialogOpen} onClose={() => setNewLoanDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle sx={{
              pb: 1,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              fontWeight: 600,
              color: 'primary.main'
            }}>Add New Loan</DialogTitle>
            <DialogContent sx={{ pt: '10px !important' }}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    label="Loan Name"
                    value={newLoan.name}
                    onChange={(e) => setNewLoan({ ...newLoan, name: e.target.value })}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Lender"
                    value={newLoan.lender}
                    onChange={(e) => setNewLoan({ ...newLoan, lender: e.target.value })}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="new-loan-type-label">Loan Type</InputLabel>
                    <Select
                      labelId="new-loan-type-label"
                      value={newLoan.type}
                      label="Loan Type"
                      onChange={(e) => setNewLoan({ ...newLoan, type: e.target.value })}
                    >
                      {loanTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="new-loan-settlement-type-label">Settlement Type</InputLabel>
                    <Select
                      labelId="new-loan-settlement-type-label"
                      value={newLoan.settlement_type}
                      label="Settlement Type"
                      onChange={(e) => setNewLoan({ ...newLoan, settlement_type: e.target.value })}
                    >
                      <MenuItem value={LOAN_SETTLEMENT_TYPES.EMI}>EMI Based</MenuItem>
                      <MenuItem value={LOAN_SETTLEMENT_TYPES.ONE_TIME}>One Time Settlement</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <AmountField
                    required
                    fullWidth
                    label="Principal Amount"
                    value={newLoan.principal_amount}
                    onChange={(e) => setNewLoan({ ...newLoan, principal_amount: e.target.value })}
                    size="small"
                    type="number"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    helperText="Enter loan amount"
                    sx={numberInputStyle}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    label="Interest Rate (%)"
                    value={newLoan.interest_rate}
                    onChange={(e) => setNewLoan({ ...newLoan, interest_rate: e.target.value })}
                    size="small"
                    type="number"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: "0",
                      max: "100",
                      step: "0.01"
                    }}
                    helperText="Annual interest rate"
                    sx={numberInputStyle}
                  />
                </Grid>
                {newLoan.settlement_type === LOAN_SETTLEMENT_TYPES.EMI && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        required
                        fullWidth
                        label="Tenure (Months)"
                        value={newLoan.tenure_months}
                        onChange={(e) => setNewLoan({ ...newLoan, tenure_months: e.target.value })}
                        size="small"
                        type="number"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: "1", step: "1" }}
                        helperText="Loan duration in months"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <AmountField
                        fullWidth
                        label="EMI Amount"
                        value={newLoan.emi_amount}
                        onChange={(e) => setNewLoan({ ...newLoan, emi_amount: e.target.value })}
                        size="small"
                        type="number"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                        helperText="Auto-calculated but can be adjusted"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Start Date"
                        value={newLoan.start_date}
                        onChange={(date) => setNewLoan({ ...newLoan, start_date: date })}
                        format="dd/MM/yyyy"
                        renderInput={(params) => <TextField {...params} fullWidth size="small" required />}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Next Payment Date"
                        value={newLoan.next_payment_date}
                        onChange={(date) => setNewLoan({ ...newLoan, next_payment_date: date })}
                        format="dd/MM/yyyy"
                        renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="End Date"
                        value={newLoan.end_date}
                        disabled
                        format="dd/MM/yyyy"
                        renderInput={(params) =>
                          <TextField {...params} fullWidth size="small" helperText="Auto-calculated" />
                        }
                      />
                    </Grid>
                  </>
                )}

                {newLoan.settlement_type === LOAN_SETTLEMENT_TYPES.ONE_TIME && (
                  <>
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        label="Start Date"
                        value={newLoan.start_date}
                        onChange={(date) => setNewLoan({ ...newLoan, start_date: date })}
                        format="dd/MM/yyyy"
                        renderInput={(params) => <TextField {...params} fullWidth size="small" required />}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        label="Loan Due Date"
                        value={newLoan.due_date}
                        onChange={(date) => setNewLoan({ ...newLoan, due_date: date })}
                        format="dd/MM/yyyy"
                        renderInput={(params) =>
                          <TextField {...params} fullWidth size="small" required helperText="Select when the loan amount is due" />
                        }
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={newLoan.description}
                    onChange={(e) => setNewLoan({ ...newLoan, description: e.target.value })}
                    size="small"
                    multiline
                    InputLabelProps={{ shrink: true }}
                    rows={3}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setNewLoanDialogOpen(false)} color="secondary">
                Cancel
              </Button>
              <Button onClick={handleCreateNewLoan} color="primary" variant="contained" disabled={loading}>
                Add Loan
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={editPaymentDialogOpen}
            onClose={() => setEditPaymentDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                p: { xs: 1, sm: 2 }
              }
            }}
          >
            <DialogTitle sx={{
              pb: 1,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              fontWeight: 600,
              color: 'primary.main'
            }}>
              Edit Payment
            </DialogTitle>
            <DialogContent sx={{ pt: '20px !important' }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <AmountField
                    fullWidth
                    label="Payment Amount"
                    value={editingPayment?.amount || ''}
                    onChange={(e) => setEditingPayment({ ...editingPayment, amount: e.target.value })}
                    size="small"
                    type="number"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    sx={[{ backgroundColor: 'background.paper' }, numberInputStyle]}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DatePicker
                    label="Payment Date"
                    value={editingPayment?.payment_date || null}
                    onChange={(date) => setEditingPayment({ ...editingPayment, payment_date: date })}
                    format="dd/MM/yyyy"
                    renderInput={(params) =>
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        sx={{ backgroundColor: 'background.paper' }}
                      />
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={editingPayment?.description || ''}
                    onChange={(e) => setEditingPayment({ ...editingPayment, description: e.target.value })}
                    size="small"
                    multiline
                    rows={3}
                    sx={{ backgroundColor: 'background.paper' }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, gap: 1 }}>
              <Button
                onClick={() => setEditPaymentDialogOpen(false)}
                color="inherit"
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSavePaymentEdit}
                color="primary"
                variant="contained"
                disabled={loading}
              >
                Save Changes
              </Button>
            </DialogActions>
          </Dialog>

          <DeleteConfirmationDialog />
          <DeletePaymentConfirmationDialog />

          {/* Amortization Details Dialog */}
          <Dialog
            open={amortizationDialogOpen}
            onClose={() => setAmortizationDialogOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                p: { xs: 1, sm: 2 }
              }
            }}
          >
            <DialogTitle sx={{
              pb: 1,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              fontWeight: 600,
              color: 'primary.main'
            }}>
              Amortization Schedule
            </DialogTitle>
            <DialogContent>
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">EMI</TableCell>
                      <TableCell align="right">Principal</TableCell>
                      <TableCell align="right">Interest</TableCell>
                      <TableCell align="right">Total Principal Paid</TableCell>
                      <TableCell align="right">Total Interest Paid</TableCell>
                      <TableCell align="right">Balance Principal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {amortizationSchedule.map((row) => (
                      <TableRow key={row.month}>
                        <TableCell>{row.month}</TableCell>
                        <TableCell align="right">{formatCurrency(row.emi)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.principalPayment)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.interestPayment)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.totalPrincipalPaid)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.totalInterestPaid)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.remainingPrincipal)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAmortizationDialogOpen(false)} color="primary">
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Advance Payment Dialog */}
          <Dialog
            open={advancePaymentDialogOpen}
            onClose={() => setAdvancePaymentDialogOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                p: { xs: 1, sm: 2 }
              }
            }}
          >
            <DialogTitle sx={{
              pb: 1,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              fontWeight: 600,
              color: 'warning.main'
            }}>
              Advance Payment
            </DialogTitle>
            <DialogContent sx={{ pt: '20px !important' }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Make an advance payment to reduce your loan burden. Choose to either reduce your EMI amount or shorten your loan tenure.
                  </Alert>
                </Grid>

                <Grid item xs={12} md={6}>
                  <AmountField
                    fullWidth
                    label="Advance Payment Amount"
                    value={advancePaymentData.amount}
                    onChange={(e) => handleAdvancePaymentChange(e.target.value, advancePaymentData.option)}
                    size="small"
                    type="number"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    helperText={`Remaining amount: ${formatCurrency(advancePaymentData.loan?.remaining_amount || 0)}`}
                    sx={numberInputStyle}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Payment Option</InputLabel>
                    <Select
                      value={advancePaymentData.option}
                      label="Payment Option"
                      onChange={(e) => handleAdvancePaymentChange(advancePaymentData.amount, e.target.value)}
                    >
                      <MenuItem value="reduce_emi">Reduce EMI Amount</MenuItem>
                      <MenuItem value="reduce_tenure">Reduce Loan Tenure</MenuItem>
                    </Select>
                    <FormHelperText>Choose how to apply the advance payment</FormHelperText>
                  </FormControl>
                </Grid>

                {advancePaymentData.amount && parseFloat(advancePaymentData.amount) > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                        Impact Analysis
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.05), rgba(33, 150, 243, 0.02))' }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Current Details
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Current EMI:</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {formatCurrency(advancePaymentData.loan?.emi_amount || 0)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Remaining Tenure:</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {(advancePaymentData.loan?.tenure_months || 0) - countEmiPayments(advancePaymentData.loan?.payments)} months
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">End Date:</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {advancePaymentData.loan?.end_date ? format(new Date(advancePaymentData.loan.end_date), 'dd/MM/yyyy') : 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.05), rgba(76, 175, 80, 0.02))' }}>
                        <Typography variant="subtitle2" color="success.main" gutterBottom fontWeight={600}>
                          After Advance Payment
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">New EMI:</Typography>
                            <Typography variant="body2" fontWeight={700} color="success.main">
                              {formatCurrency(advancePaymentData.newEMI)}
                              {advancePaymentData.option === 'reduce_emi' && (
                                <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1 }}>
                                  (↓ {formatCurrency((advancePaymentData.loan?.emi_amount || 0) - advancePaymentData.newEMI)})
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">New Remaining Tenure:</Typography>
                            <Typography variant="body2" fontWeight={700} color="success.main">
                              {advancePaymentData.remainingTenure} months
                              {advancePaymentData.option === 'reduce_tenure' && (
                                <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1 }}>
                                  (↓ {((advancePaymentData.loan?.tenure_months || 0) - countEmiPayments(advancePaymentData.loan?.payments)) - advancePaymentData.remainingTenure} months)
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">New End Date:</Typography>
                            <Typography variant="body2" fontWeight={700} color="success.main">
                              {advancePaymentData.newEndDate ? format(advancePaymentData.newEndDate, 'dd/MM/yyyy') : 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>

                    <Grid item xs={12}>
                      <Paper sx={{ 
                        p: 2, 
                        background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1), rgba(255, 152, 0, 0.05))',
                        border: '2px solid rgba(255, 152, 0, 0.3)'
                      }}>
                        <Typography variant="subtitle1" fontWeight={700} color="warning.main" gutterBottom>
                          💰 Total Interest Savings
                        </Typography>
                        <Typography variant="h5" fontWeight={700} color="warning.main">
                          {formatCurrency(advancePaymentData.savings || 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          You will save this amount in interest over the remaining loan period
                        </Typography>
                      </Paper>
                    </Grid>
                  </>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, gap: 1 }}>
              <Button
                onClick={() => setAdvancePaymentDialogOpen(false)}
                color="inherit"
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitAdvancePayment}
                color="warning"
                variant="contained"
                disabled={loading || !advancePaymentData.amount || parseFloat(advancePaymentData.amount) <= 0}
              >
                {loading ? 'Processing...' : 'Confirm Advance Payment'}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={statusSnackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
          >
            <Alert onClose={handleCloseSnackbar} severity={statusSnackbar.severity} sx={{ width: '100%' }}>
              {statusSnackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
      <GuidedTour steps={TOURS.loans} open={loansTour.open} onClose={loansTour.close} />
    </LocalizationProvider>
  );
};

export default Loans;
